import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { ProductRepository } from '../repositories/ProductRepository';
import { Product } from '../domain/entities/Product';
import { IProductCrossSell } from '../../exported/product-cross-sell/ProductCrossSell';
import { emit, events } from '@estee/elc-service-bus';
import { QueryFragments } from '~api/QueryFragments';
import { IFeatures } from '~constants/Features';
import { IItem } from '~api/interfaces/IItem';
import { ItemTypes } from '~constants/ItemTypes';
import { sortByCrossSellOrder } from '~utils/SortByCrossSellOrder';

interface IUniqueCrossSellSku {
    [key: string]: IItem;
}

export interface IProductCrossellViewControllerData {
    crossSellProducts: Product[];
}

export class ProductCrossSellViewController extends ViewController {
    @observable private crossSellProducts: Product[];
    @observable isLoaded: boolean = false;
    @observable hasCrossSellProducts: boolean = false;
    private productRepository: ProductRepository;
    public dataState: IProductCrossellViewControllerData;
    @observable private queryFragments: QueryFragments;

    constructor(productRepository: ProductRepository, queryFragments: QueryFragments) {
        super();

        this.productRepository = productRepository;
        this.queryFragments = queryFragments;
    }

    @computed
    public get data(): IProductCrossellViewControllerData {
        return {
            crossSellProducts: this.crossSellProducts
        };
    }

    private getUniqueSkus(products: Product[] = []) {
        const uniqueCrossSellSkus: IUniqueCrossSellSku = {};

        products.forEach((product) => {
            product.crossSell.forEach(({ skuId }) => {
                if (skuId) {
                    uniqueCrossSellSkus[skuId] = {
                        type: ItemTypes.sku,
                        params: {
                            id: skuId,
                            defaultSkuId: skuId
                        }
                    };
                }
            });
        });

        return Object.values(uniqueCrossSellSkus);
    }

    @action
    private finishLoading(products: Product[]) {
        const productIds = products.map(p => p.productId);
    
        this.hasCrossSellProducts = productIds && productIds.length > 0;
        this.isLoaded = true;
    
        emit(events.PRODUCT_LOADED, productIds);
        emit(events.PRODUCT_GRID_VIEWED, { products });
    
        this.isLoading = false;
    }

    @action
    public loaded = async (props: IProductCrossSell) => {
        this.isLoading = true;
        const { features } = props.config;

        this.queryFragments.setFeatures(<IFeatures>features);

        const products = await this.productRepository.getProductsByItems(
            (props.config.items || []) as IItem[],
            {
                queryFragments: this.queryFragments.queryFragments
            }
        );

        emit(
            events.PRODUCT_LOADED,
            products.map((product) => product.productId)
        );
        const uniqueSkus = this.getUniqueSkus(products);

        if (!uniqueSkus.length) {
            this.finishLoading([]);
            return;
        }

        const includeInnerHits = false;
        const crossSellProducts = await this.productRepository.getProductsByItems(uniqueSkus, {
            includeInnerHits
        });
        const crossSellItems = products[0].crossSell;

        this.crossSellProducts = sortByCrossSellOrder(crossSellProducts, crossSellItems);

        this.finishLoading(crossSellProducts);
    };
}
